import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { formatDate, isGameMaster, cn } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Clock,
  MapPin,
  ChevronRight,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = '/api';

export default function SessionsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [date, setDate] = useState(new Date());
  
  const [newSession, setNewSession] = useState({
    title: '',
    scheduled_date: new Date().toISOString(),
    location: '',
    estimated_duration: 240,
    agenda: [],
    mj_prep_notes: ''
  });

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    fetchSessions();
  }, [campaignId]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/sessions`);
      setSessions(response.data);
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSession.title.trim()) {
      toast.error(t('error_required', language));
      return;
    }

    setCreating(true);
    try {
      const sessionData = {
        ...newSession,
        scheduled_date: date.toISOString(),
        agenda: newSession.agenda.filter(a => a.trim())
      };
      
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/sessions`, sessionData);
      toast.success(t('success_saved', language));
      setCreateOpen(false);
      setNewSession({
        title: '',
        scheduled_date: new Date().toISOString(),
        location: '',
        estimated_duration: 240,
        agenda: [],
        mj_prep_notes: ''
      });
      navigate(`/campaigns/${campaignId}/sessions/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  // Group sessions by month
  const groupedSessions = sessions.reduce((acc, session) => {
    const monthKey = format(new Date(session.scheduled_date), 'MMMM yyyy', {
      locale: language === 'fr' ? fr : enUS
    });
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(session);
    return acc;
  }, {});

  return (
    <MainLayout>
      <div className="space-y-8 animate-fade-in" data-testid="sessions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">{t('sessions', language)}</h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Planifiez et documentez vos sessions de jeu'
                : 'Plan and document your game sessions'}
            </p>
          </div>
          
          {isMJ && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="fantasy-button" data-testid="create-session-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('session_create', language)}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <form onSubmit={handleCreate}>
                  <DialogHeader>
                    <DialogTitle className="font-cinzel">{t('session_create', language)}</DialogTitle>
                    <DialogDescription>
                      {language === 'fr' 
                        ? 'Planifiez votre prochaine session de jeu'
                        : 'Plan your next game session'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label htmlFor="session-title">{t('session_title', language)} *</Label>
                      <Input
                        id="session-title"
                        placeholder={language === 'fr' ? 'Session 1 - L\'aventure commence' : 'Session 1 - The adventure begins'}
                        value={newSession.title}
                        onChange={(e) => setNewSession(prev => ({ ...prev, title: e.target.value }))}
                        required
                        data-testid="session-title-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t('session_date', language)}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !date && "text-muted-foreground"
                            )}
                            data-testid="session-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, 'PPP', { locale: language === 'fr' ? fr : enUS }) : (
                              language === 'fr' ? 'Choisir une date' : 'Pick a date'
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(d) => d && setDate(d)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="session-location">{t('session_location', language)}</Label>
                      <Input
                        id="session-location"
                        placeholder={language === 'fr' ? 'Discord / Chez Jean' : 'Discord / John\'s place'}
                        value={newSession.location}
                        onChange={(e) => setNewSession(prev => ({ ...prev, location: e.target.value }))}
                        data-testid="session-location-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="session-duration">{t('session_duration', language)}</Label>
                      <Input
                        id="session-duration"
                        type="number"
                        min={30}
                        max={720}
                        value={newSession.estimated_duration}
                        onChange={(e) => setNewSession(prev => ({ ...prev, estimated_duration: parseInt(e.target.value) || 240 }))}
                        data-testid="session-duration-input"
                      />
                    </div>
                    
                    {isMJ && (
                      <div className="space-y-2">
                        <Label htmlFor="session-prep">{t('session_prep', language)}</Label>
                        <Textarea
                          id="session-prep"
                          placeholder={language === 'fr' 
                            ? 'Notes de préparation pour cette session...'
                            : 'Preparation notes for this session...'}
                          value={newSession.mj_prep_notes}
                          onChange={(e) => setNewSession(prev => ({ ...prev, mj_prep_notes: e.target.value }))}
                          rows={4}
                          data-testid="session-prep-input"
                        />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                      {t('cancel', language)}
                    </Button>
                    <Button type="submit" disabled={creating} className="fantasy-button" data-testid="create-session-submit">
                      {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('create', language)}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Sessions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="glass-card">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-muted rounded w-1/3" />
                    <div className="h-4 bg-muted rounded w-1/4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-16 text-center">
              <CalendarIcon className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
              <h3 className="font-cinzel text-xl font-bold mb-2">
                {language === 'fr' ? 'Aucune session' : 'No sessions'}
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {language === 'fr'
                  ? 'Créez votre première session pour commencer à documenter votre aventure'
                  : 'Create your first session to start documenting your adventure'}
              </p>
              {isMJ && (
                <Button onClick={() => setCreateOpen(true)} className="fantasy-button">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('session_create', language)}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedSessions).map(([month, monthSessions]) => (
              <div key={month} className="space-y-4">
                <h2 className="font-cinzel text-lg font-bold text-muted-foreground capitalize">{month}</h2>
                <div className="space-y-3">
                  {monthSessions.map((session) => (
                    <Card 
                      key={session.id} 
                      className="glass-card card-interactive cursor-pointer"
                      onClick={() => navigate(`/campaigns/${campaignId}/sessions/${session.id}`)}
                      data-testid={`session-card-${session.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-medium">{session.title}</h3>
                              {session.is_published ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-success/10 text-success">
                                  <Eye className="h-3 w-3" />
                                  {t('session_published', language)}
                                </span>
                              ) : isMJ ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                                  <EyeOff className="h-3 w-3" />
                                  {t('session_draft', language)}
                                </span>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {formatDate(session.scheduled_date, language === 'fr' ? 'fr-FR' : 'en-US')}
                              </span>
                              {session.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {session.location}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {Math.floor(session.estimated_duration / 60)}h{session.estimated_duration % 60 > 0 ? `${session.estimated_duration % 60}m` : ''}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}

